import React, { useRef, useState } from 'react';
import { Alert, Box, IconButton, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { IRegistrationFormField } from '../../types/global/forms.types';
import { DemoReelGrid } from '../WorkProfile/DemoReelGrid';
import { useThemeContext } from '../../hook/useThemeManager';
import { AudioReelGrid } from '../WorkProfile/AudioReelGrid';
import { UserProjectSearchDialogue } from '../UserProjectSearchDialogue/UserProjectSearchDialogue';
import { FormGenerator } from '../FormGenerator/FormGenerator';
import { createSystemFormSelector } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { FORM_IDENTIFIER_ENUM } from '../../types/global/helper';
import { ICreateNewSound } from '../../redux/project/types';
import { useDispatchPromise } from '../../utils/AppUtils';
import { createNewSound } from '../../redux/project/actions';
import { SentryCapture } from '../../analytics/Sentry';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

enum MediaType {
    Projects = 'projects',
    Playlists = 'playlists',
    Sounds = 'sounds',
    Pictures = 'pictures',
}

type LowercaseMediaType = Lowercase<keyof typeof MediaType>;

export interface IMediaAdderComponent {
    mediaType?: LowercaseMediaType;
    mediaValues: any[];
    onAddClicked?: () => void;
    onDuplicate?: (actionValue: any) => void;
    onDelete?: (actionValue: any) => void;
    onSubmit?: (formData: any) => void;
    initialValue?: any;
    formField?: IRegistrationFormField;
    isEdit?: boolean;
    title?: string;
    maxSelection?: number;
    disableAddButton?: boolean;
}

function getMediaType(type: MediaType): 'video' | 'audio' | 'picture' {
    const mediaMap: Record<MediaType, 'video' | 'audio' | 'picture'> = {
        [MediaType.Projects]: 'video',
        [MediaType.Playlists]: 'video',
        [MediaType.Sounds]: 'audio',
        [MediaType.Pictures]: 'picture',
    };

    return mediaMap[type];
}

const MediaAdderComponent: React.FC<IMediaAdderComponent> = ({
    mediaType,
    mediaValues,
    onDelete,
    onSubmit,
    isEdit = true,
    title,
    maxSelection,
    disableAddButton,
}) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatchPromise();
    const audioRef = useRef(new Audio());

    const [openSearch, setOpenSearch] = useState<Record<string, boolean>>({
        projects: false,
        sounds: false,
        playlists: false,
        pictures: false,
    });

    const [loading, setLoading] = useState<boolean>(false);
    const [openForm, setOpenForm] = useState<boolean>(false);

    const systemFormsByTypeSelector = createSystemFormSelector(
        form => form.identifier === FORM_IDENTIFIER_ENUM.CREATE_NEW_SOUND,
    );
    const specificSystemForms = useSelector(systemFormsByTypeSelector);

    const extractAudioMetadata = (file: File): Promise<HTMLAudioElement> => {
        return new Promise((resolve, reject) => {
            const audio = audioRef.current;
            const objectURL = URL.createObjectURL(file);

            audio.src = objectURL;
            audio.onloadedmetadata = () => {
                resolve(audio);
                URL.revokeObjectURL(objectURL); // Cleanup object URL
            };

            audio.onerror = () => reject(new Error('Failed to load audio metadata'));
        });
    };

    const handleCreateNewSound = async (payload: ICreateNewSound) => {
        try {
            setLoading(true);
            const audio = await extractAudioMetadata(payload.sound);

            const body: ICreateNewSound = {
                ...payload,
                duration: audio.duration,
                additional_details: {
                    ...audio,
                },
            };

            await dispatch(createNewSound(body));
            setLoading(false);
            setOpenForm(!openForm);
        } catch (error) {
            SentryCapture(error);
            setLoading(false);
            setOpenForm(!openForm);
        }
    };

    if (mediaType === 'projects') {
        return (
            <Stack direction="column">
                <UserProjectSearchDialogue
                    open={openSearch.projects}
                    type={getMediaType(mediaType as MediaType)}
                    onClose={() => setOpenSearch(prev => ({ ...prev, projects: false }))}
                    onSubmit={onSubmit}
                    maxSelection={maxSelection}
                />
                {!!mediaValues && (
                    <DemoReelGrid
                        themeStyle={themeStyle}
                        projects={mediaValues}
                        onDelete={onDelete}
                        isEditMode={true}
                        title={title}
                        disableAddButton={disableAddButton}
                    />
                )}

                <AddIcon
                    fontSize="small"
                    onClick={() => setOpenSearch(prev => ({ ...prev, projects: true }))}
                    sx={{
                        mt: 1,
                        width: '100%',
                        height: 50,
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '10px',
                        color: theme[themeStyle].highlight,
                        cursor: 'pointer',
                    }}
                />
                <Alert severity="info" sx={{ borderRadius: 2, my: 2, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    You can select an already existing project or use the create button to upload a new project and it
                    will appear here. You are only allowed one entry.
                </Alert>
            </Stack>
        );
    }

    if (mediaType === 'sounds') {
        return (
            <Stack direction="column">
                <UserProjectSearchDialogue
                    open={openSearch.sounds}
                    type={getMediaType(mediaType as MediaType)}
                    onClose={() => setOpenSearch(prev => ({ ...prev, sounds: false }))}
                    onSubmit={onSubmit}
                    enableAdd
                    onAddClick={() => setOpenForm(!openForm)}
                    maxSelection={maxSelection}
                />
                {specificSystemForms && (
                    <FormGenerator
                        open={openForm}
                        formFields={specificSystemForms?.form!}
                        onClose={() => setOpenForm(!openForm)}
                        onSubmit={handleCreateNewSound}
                        manualClose
                        header={specificSystemForms?.title}
                        loading={loading}
                    />
                )}
                {!!mediaValues && (
                    <AudioReelGrid themeStyle={themeStyle} sounds={mediaValues} isEditMode onDelete={onDelete} />
                )}

                <AddIcon
                    fontSize="small"
                    onClick={() => setOpenSearch(prev => ({ ...prev, sounds: true }))}
                    sx={{
                        mt: 1,
                        width: '100%',
                        height: 50,
                        backgroundColor: theme[themeStyle].deep_grey,
                        borderRadius: '10px',
                        color: theme[themeStyle].highlight,
                        cursor: 'pointer',
                    }}
                />
                <Alert severity="info" sx={{ borderRadius: 2, my: 2, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    You can select an already existing sound or use the + button to upload a new sound and it will
                    appear here. You are only allowed one entry.
                </Alert>
            </Stack>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
            }}>
            <UserProjectSearchDialogue
                type={getMediaType(mediaType as MediaType)}
                open={openSearch[mediaType || 'projects']}
                onClose={() => setOpenSearch(prev => ({ ...prev, [mediaType || 'projects']: false }))}
                onSubmit={onSubmit}
                maxSelection={maxSelection}
            />
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
                <IconButton
                    onClick={() => setOpenSearch(prev => ({ ...prev, [mediaType || 'projects']: true }))}
                    sx={{
                        width: 60,
                        height: 60,
                        backgroundColor: '#f5f5f5',
                        borderRadius: '2px',
                        '&:hover': { backgroundColor: '#e0e0e0' },
                    }}>
                    <AddIcon fontSize="large" color="primary" />
                </IconButton>
            </Box>
        </Box>
    );
};

export default MediaAdderComponent;
